"use client";

import { useRef, useState } from "react";
import { track } from "@lib/track";
import MarketingOptIn from "./MarketingOptIn";

/**
 * Waitlist capture for a retreat that has sold out.
 *
 * The Autumn Equinox retreat sold out and kept rendering a "Book Your Place"
 * button straight through to the host's checkout. 23 sessions went there in a
 * month to find out for themselves; the page offered no way to leave an address,
 * so not one of them became a lead. This is the replacement CTA.
 *
 * Posts to `/api/retreat-interest` with `mode: "waitlist"` — the same chokepoint
 * every other retreat lead goes through, so there is still exactly one place
 * that resolves a retreat slug and decides what gets emailed. The server
 * downgrades a brochure request on a sold-out retreat to the waitlist anyway, so
 * this component cannot be used to send someone a "reserve your place" email for
 * a retreat that can't take them.
 *
 * Tracking: `retreat_waitlist` fires **only** after the API confirms — never on
 * submit, never on the error path. Deliberately NOT `retreat_interest`: that
 * event is the brochure funnel's success step and reports read it as such, so
 * blending waitlist signups into it would make brochure conversion look better
 * than it is. Label is the retreat id in both, so they still group on one key.
 */

type Props = {
  retreatId: string;
  retreatName: string;
  component: string;
  /** Rendered expanded (a page section) rather than behind a button (a card). */
  defaultOpen?: boolean;
  /** Button copy when collapsed. */
  cta?: string;
  /** Light type for use on a dark photographic background. */
  tone?: "light" | "dark";
  /**
   * Register-interest mode: the visitor picks one of these instead of the form
   * being fixed to `retreatId`, and the pick is what gets posted and tracked.
   * Used where no retreat is scheduled (`RETREAT_INTEREST_LISTS`), so the copy
   * props below replace the "this retreat is full" wording.
   */
  choices?: { id: string; label: string }[];
  heading?: string;
  intro?: string;
  doneText?: string;
  submitLabel?: string;
};

export default function RetreatWaitlist({
  retreatId,
  retreatName,
  component,
  defaultOpen = false,
  cta = "Join the waitlist",
  tone = "dark",
  choices,
  heading = "This retreat is full — join the waitlist",
  intro = "We\u2019ll email you first if a place is released, and when the next date opens.",
  doneText,
  submitLabel = "Add me to the waitlist",
}: Props) {
  const [choice, setChoice] = useState<string | null>(null);
  // Unticked by default. Joining a waitlist is a soft opt-in (follow-ups only);
  // only this tick puts someone on the newsletter.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [open, setOpen] = useState(defaultOpen);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState(""); // honeypot — the server makes the real decision
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const openedAt = useRef<number>(defaultOpen ? Date.now() : 0);

  const openForm = () => {
    openedAt.current = Date.now();
    setOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // one capture per submit, so one retreat_waitlist event
    // In register-interest mode the pick IS the list, so it is the id posted and
    // the event label. Nothing is sent without one.
    const targetId = choices ? choice : retreatId;
    if (!targetId) {
      setError("Please choose which kind of retreat you\u2019re interested in.");
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/retreat-interest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          retreatId: targetId,
          mode: "waitlist",
          marketingOptIn,
          hp,
          elapsedMs: Date.now() - openedAt.current,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        setError(data?.message || "Something went wrong. Please try again.");
        return; // retreat_waitlist must NOT fire on the error path
      }

      try {
        track(
          "retreat_waitlist",
          { label: targetId, component },
          { event: "retreat_waitlist", label: targetId, component }
        );
      } catch {}

      setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const light = tone === "light";
  const cardCls = light
    ? "rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm p-5 text-left"
    : "rounded-xl border border-[var(--border-on-light)] bg-[var(--bg-surface-dk)] p-5 text-left";
  const headingCls = light ? "text-white" : "text-[var(--color-text-brand)]";
  const bodyCls = light ? "text-white/75" : "text-[var(--text-on-light-muted)]";
  // Inputs stay on a solid surface in both tones — a translucent field over a
  // photograph is unreadable on a phone in daylight.
  const inputCls =
    "block w-full rounded-lg p-3 text-base border border-[var(--border-on-light)] bg-[var(--bg-surface-dk)] text-[var(--color-text-ink)] focus:border-[var(--color-brand)] focus:ring-1 focus:ring-[var(--color-brand)] outline-none";

  if (done) {
    return (
      <div className={cardCls}>
        <p className={`text-sm font-semibold ${headingCls} mb-1`}>You&rsquo;re on the list</p>
        <p className={`text-sm ${bodyCls}`}>
          {doneText ?? (
            <>
              We&rsquo;ll email you if a place is released for {retreatName}, and as soon as the
              next date opens. Check your inbox for confirmation.
            </>
          )}
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={openForm}
        className={
          light
            ? "inline-flex items-center justify-center rounded-full bg-white text-[var(--color-text-brand)] px-6 py-3 text-sm font-bold hover:bg-white/90 transition"
            : "inline-flex items-center justify-center rounded-full border border-[var(--color-text-brand)] text-[var(--color-text-brand)] px-5 py-2.5 text-sm font-semibold hover:bg-[var(--color-text-brand)] hover:text-[var(--cream)] transition"
        }
      >
        {cta}
      </button>
    );
  }

  return (
    <form onSubmit={submit} className={cardCls}>
      <p className={`text-sm font-semibold ${headingCls} mb-1`}>{heading}</p>
      <p className={`text-xs ${bodyCls} mb-3`}>{intro}</p>

      {choices && (
        <fieldset className="mb-3">
          <legend className={`text-xs font-semibold ${headingCls} mb-2`}>I&rsquo;m interested in</legend>
          <div className="flex flex-wrap gap-2">
            {choices.map((c) => {
              const selected = choice === c.id;
              return (
                <label
                  key={c.id}
                  className={`cursor-pointer rounded-full border px-4 py-2 text-sm font-semibold transition has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--color-brand)] has-[:focus-visible]:ring-offset-2 ${
                    selected
                      ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-white"
                      : light
                        ? "border-white/40 text-white hover:border-white"
                        : "border-[var(--border-on-light)] text-[var(--color-text-ink)] hover:border-[var(--color-brand)]"
                  }`}
                >
                  <input
                    type="radio"
                    name={`wl-choice-${retreatId}`}
                    value={c.id}
                    checked={selected}
                    onChange={() => {
                      setChoice(c.id);
                      setError(null);
                    }}
                    className="sr-only"
                  />
                  {c.label}
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      <label className="sr-only" htmlFor={`wl-name-${retreatId}`}>Name</label>
      <input
        id={`wl-name-${retreatId}`}
        type="text"
        required
        autoComplete="name"
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className={`${inputCls} mb-2`}
      />

      <label className="sr-only" htmlFor={`wl-email-${retreatId}`}>Email</label>
      <input
        id={`wl-email-${retreatId}`}
        type="email"
        required
        autoComplete="email"
        placeholder="Your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={`${inputCls} mb-3`}
      />

      {/* Honeypot — hidden from people, irresistible to bots. */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        className="absolute left-[-9999px] w-px h-px opacity-0"
      />

      <MarketingOptIn checked={marketingOptIn} onChange={setMarketingOptIn} light={light} className="mb-3" />

      {error && (
        <p role="alert" className="text-xs text-red-500 mb-2">{error}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-lg bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] py-3 px-4 font-bold text-sm hover:bg-[var(--btn-primary-hover)] transition disabled:opacity-60"
      >
        {submitting ? "Adding you…" : submitLabel}
      </button>
    </form>
  );
}
